import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PropsWithChildren, useContext } from "react";
import { IntlProvider } from "react-intl";
import { MapProvider } from "react-map-gl/maplibre";

import { MetaModelsProvider, ldoApiV1 } from "@nens/ldo/api-v1";
import { LdoAuthProvider } from "@nens/ldo/auth-v1";
import { ChooseTenant, LoadingSpinner } from "@nens/ldo/components";
import translations from "@nens/ldo/languages";
import { LocaleContext, LocaleProvider } from "@nens/react/contexts";
import { DEFAULT_QUERY_OPTIONS } from "@nens/react-query-utils";
import { SentryErrorBoundary, initSentry } from "@nens/sentry";

import Header from "../header/header";
import Routes from "../routes/routes";

import Fonts from "./fonts";
import { theme } from "./theme";

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: DEFAULT_QUERY_OPTIONS,
  },
});

export function WithIntl({ children }: PropsWithChildren) {
  const { locale } = useContext(LocaleContext);

  return (
    <IntlProvider key={locale} locale={locale} defaultLocale="en" messages={translations[locale]}>
      {children}
    </IntlProvider>
  );
}

export function App() {
  return (
    <SentryErrorBoundary appName="ldo">
      <ChakraProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <LdoAuthProvider
            loadingComponent={<LoadingSpinner />}
            chooseTenantComponent={(tenants, setTenant) => (
              <ChooseTenant tenants={tenants} setTenant={setTenant} />
            )}
            setTenantAndAuthToken={ldoApiV1.setTenantAndAuthToken.bind(ldoApiV1)}
          >
            <MetaModelsProvider loadingComponent={<LoadingSpinner />}>
              <LocaleProvider supportedLocales={["en", "nl"]} defaultLocale="nl">
                <WithIntl>
                  <MapProvider>
                    <Fonts />
                    <Header />
                    <Routes />
                  </MapProvider>
                </WithIntl>
              </LocaleProvider>
            </MetaModelsProvider>
          </LdoAuthProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </SentryErrorBoundary>
  );
}

export default App;
